export type FractureDesignSummary = {
  id: number;
  name?: string;
  notes?: string;
  is_active: boolean;
  created_at?: string;
  stage_count: number;
  cluster_per_stage: number;
  cluster_spacing?: number;
  stage_spacing: number;
  pump_rate: number;
  total_fluid: number;
  total_proppant: number;
  fluid_type: string;
  proppant_type: string;
  max_proppant_conc: number;
};

export const fractureDesignDisplayKey = (design: FractureDesignSummary) => design.notes?.startsWith('[DRAFT]')
  ? `draft:${design.id}`
  : JSON.stringify([
  design.stage_count,
  design.cluster_per_stage,
  design.cluster_spacing,
  design.stage_spacing,
  design.pump_rate,
  design.total_fluid,
  design.total_proppant,
  design.fluid_type,
  design.proppant_type,
  design.max_proppant_conc,
]);

const bucket = (value: number | undefined, step: number) => {
  const numeric = Number(value ?? 0);
  return Number.isFinite(numeric) ? Math.round(numeric / step) * step : 0;
};

const normalizedSystem = (value: string) => String(value || '未设计')
  .trim()
  .toLowerCase()
  .replace(/[\s（）()、/＋+_-]+/g, '');

const representativeFamily = (value?: string) => {
  const name = String(value || '')
    .replace(/[（(]\d+[）)]\s*$/g, '')
    .replace(/\s*\d{7,8}\s+\d{1,2}:\d{2}(?::\d{2})?\s*$/g, '')
    .replace(/(?:微调方案)+/g, '')
    .replace(/\s+/g, '');
  if (name.includes('智能压裂方案')) return name.slice(0, name.indexOf('智能压裂方案') + '智能压裂方案'.length);
  if (name === '自动设计甜点') return name;
  return '';
};

/**
 * Groups small parameter tweaks into one representative engineering scheme.
 * Stage/cluster counts and material systems remain exact; continuous parameters
 * use engineering-sized bands so genuinely different scales stay visible.
 */
export const fractureDesignRepresentativeKey = (design: FractureDesignSummary) => {
  if (design.notes?.startsWith('[DRAFT]')) return 'representative:draft';
  const stageCount = Math.max(1, Number(design.stage_count) || 1);
  const family = representativeFamily(design.name);
  if (family) return JSON.stringify(['family', family]);
  return JSON.stringify([
    'representative',
    design.stage_count,
    design.cluster_per_stage,
    bucket(design.stage_spacing, 20),
    bucket(design.cluster_spacing, 5),
    bucket(design.pump_rate, 2),
    bucket(design.total_fluid / stageCount, 50),
    bucket(design.total_proppant / stageCount, 5),
    normalizedSystem(design.fluid_type),
    normalizedSystem(design.proppant_type),
    bucket(design.max_proppant_conc, 50),
  ]);
};

export const deduplicateFractureDesigns = <T extends FractureDesignSummary>(designs: T[]): T[] => {
  const preferred = [...designs].sort((a, b) => {
    if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
    return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
  });
  const seen = new Set<string>();
  return preferred.filter((design) => {
    const key = fractureDesignRepresentativeKey(design);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
